import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Typography,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Collapse,
  TablePagination
} from '@mui/material';
import {
  Sort,
  Search,
  ExpandMore,
  ExpandLess,
  Warning,
  Error,
  Download,
  Delete
} from '@mui/icons-material';
import { collection, getDocs, query, orderBy, limit, startAfter, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { database } from '../Utils/firebaseConfig';
import { useAuth } from '../Hooks/useAuth';

const DataAnalysisTable = () => {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterField, setFilterField] = useState('all');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(200);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [duplicates, setDuplicates] = useState([]);
  const [showOnlyDuplicates, setShowOnlyDuplicates] = useState(false);
  const [fetchProgress, setFetchProgress] = useState({ current: 0, total: 0, message: '' });
  const [isFetching, setIsFetching] = useState(false);
  const [stopRequested, setStopRequested] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState({ current: 0, total: 0, message: '' });
  const [isDeleting, setIsDeleting] = useState(false);
  const stopRequestedRef = useRef(false);

  // Get the database collection name - ALWAYS use production
  const getDatabaseCollection = useCallback(() => {
    // ALWAYS use production collection for data analysis
    const collectionName = 'production';
    
    console.log('DataAnalysisTable: Using PRODUCTION collection:', collectionName);
    
    return collectionName;
  }, []);

  // Helper function to safely convert dates
  const safeDateConvert = useCallback((dateValue) => {
    if (!dateValue) return null;
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      return dateValue.toDate();
    }
    if (dateValue instanceof Date) {
      return dateValue;
    }
    if (typeof dateValue === 'string' || typeof dateValue === 'number') {
      return new Date(dateValue);
    }
    return null;
  }, []);

  // Fetch data for ID duplicate checking - search ENTIRE database for each ID with PARALLEL processing
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setIsFetching(true);
      setStopRequested(false);
      stopRequestedRef.current = false;
      
      const collectionName = getDatabaseCollection();
      console.log('Fetching data from collection:', collectionName);
      
      // Step 1: Get 30 recent events to sample IDs
      setFetchProgress({ current: 0, total: 30, message: 'Fetching recent events...' });
      
      const sampleQuery = query(
        collection(database, collectionName),
        orderBy('createdAt', 'desc'),
        limit(30)
      );
      
      const sampleSnapshot = await getDocs(sampleQuery);
      const sampleEvents = sampleSnapshot.docs.map(doc => ({
        firestoreId: doc.id,
        ...doc.data(),
        start: safeDateConvert(doc.data().start),
        end: safeDateConvert(doc.data().end),
        createdAt: safeDateConvert(doc.data().createdAt),
        updatedAt: safeDateConvert(doc.data().updatedAt),
      })).filter(event => event.id);
      
      if (sampleEvents.length === 0) {
        setData([]);
        setDuplicates([]);
        return;
      }
      
      // Step 2: Get unique IDs and process them in PARALLEL
      const uniqueIds = [...new Set(sampleEvents.map(event => event.id))];
      setFetchProgress({ current: 0, total: uniqueIds.length, message: `Searching ENTIRE database for ${uniqueIds.length} IDs in PARALLEL...` });
      
      console.log(`Processing ${uniqueIds.length} IDs in parallel for maximum speed`);
      
      // PARALLEL PROCESSING: Search for all IDs simultaneously
      const searchPromises = uniqueIds.map(async (currentId, index) => {
        if (stopRequestedRef.current) return null;
        
        // Update progress for this ID
        setFetchProgress({
          current: index + 1,
          total: uniqueIds.length,
          message: `Searching for ID: ${currentId} (${index + 1}/${uniqueIds.length})`
        });
        
        console.log(`Searching ENTIRE database for ID: ${currentId} (${index + 1}/${uniqueIds.length})`);
        
        // Search the ENTIRE database for this specific ID using pagination
        let allEventsWithId = [];
        let lastDoc = null;
        let hasMore = true;
        const searchBatchSize = 1000; // Large batches for efficiency
        
        while (hasMore && !stopRequestedRef.current) {
          let searchQuery;
          if (lastDoc) {
            searchQuery = query(
              collection(database, collectionName),
              orderBy('createdAt', 'desc'),
              startAfter(lastDoc),
              limit(searchBatchSize)
            );
          } else {
            searchQuery = query(
              collection(database, collectionName),
              orderBy('createdAt', 'desc'),
              limit(searchBatchSize)
            );
          }
          
          const searchSnapshot = await getDocs(searchQuery);
          const searchEvents = [];
          
          searchSnapshot.forEach((doc) => {
            const docData = doc.data();
            // EXACT string match - docData.id must be exactly equal to currentId
            if (docData.id === currentId && !docData.not_contaminated) {
              searchEvents.push({
                firestoreId: doc.id,
                ...docData,
                start: safeDateConvert(docData.start),
                end: safeDateConvert(docData.end),
                createdAt: safeDateConvert(docData.createdAt),
                updatedAt: safeDateConvert(docData.updatedAt),
              });
            }
          });
          
          allEventsWithId.push(...searchEvents);
          
          // Check if we have more documents to fetch
          if (searchSnapshot.docs.length < searchBatchSize) {
            hasMore = false;
          } else {
            lastDoc = searchSnapshot.docs[searchSnapshot.docs.length - 1];
          }
          
          console.log(`Search batch: Found ${searchEvents.length} events with ID ${currentId}, total so far: ${allEventsWithId.length}`);
        }
        
        console.log(`Found ${allEventsWithId.length} total events with ID: ${currentId}`);
        
        // Return the duplicate group if there are duplicates
        if (allEventsWithId.length > 1) {
          // Sort by createdAt to find the original (earliest)
          const sortedRecords = allEventsWithId.sort((a, b) => {
            const aDate = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt || 0);
            const bDate = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt || 0);
            return aDate.getTime() - bDate.getTime();
          });
          
          return {
            type: 'Duplicate ID',
            field: 'id',
            value: currentId,
            count: allEventsWithId.length,
            original: sortedRecords[0],
            duplicates: sortedRecords.slice(1),
            records: allEventsWithId
          };
        }
        
        return null; // No duplicates for this ID
      });
      
      // Wait for ALL parallel searches to complete
      console.log('Waiting for all parallel searches to complete...');
      const allResults = await Promise.all(searchPromises);
      
      // Filter out null results and collect duplicate groups
      const allDuplicateGroups = allResults.filter(result => result !== null);
      
      console.log(`Parallel processing complete! Found ${allDuplicateGroups.length} duplicate groups`);
      
      // Set data and find duplicates
      const allEvents = allDuplicateGroups.flatMap(group => group.records);
      const uniqueEvents = allEvents.filter(event => event && event.id);
      
      setData(uniqueEvents);
      findDuplicates(uniqueEvents);
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setIsFetching(false);
      setFetchProgress({ current: 0, total: 0, message: '' });
    }
  }, [getDatabaseCollection, safeDateConvert]);
  
  // Function to stop the fetch process
  const stopFetch = () => {
    console.log('User requested to stop fetching');
    setStopRequested(true);
    stopRequestedRef.current = true;
    
    // Force immediate UI update
    setFetchProgress(prev => ({
      ...prev,
      message: 'Stopping fetch process...'
    }));
    
    // Force loading state to false after a short delay to ensure stop is processed
    setTimeout(() => {
      if (stopRequestedRef.current) {
        setLoading(false);
        setIsFetching(false);
      }
    }, 100);
  };

  // Find duplicate IDs and identify the original (earliest createdAt)
  const findDuplicates = (records) => {
    const duplicateGroups = [];
    
    // Group by ID (most important)
    const idGroups = {};
    records.forEach(record => {
      if (record.id) {
        if (!idGroups[record.id]) idGroups[record.id] = [];
        idGroups[record.id].push(record);
      }
    });
    
    Object.entries(idGroups).forEach(([id, group]) => {
      if (group.length > 1) {
        console.log(`Processing duplicate group for ID: ${id} with ${group.length} records`);
        
        // Sort by createdAt to find the original (earliest)
        const sortedRecords = group.sort((a, b) => {
          // Ensure we have valid dates for comparison
          const aDate = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt || 0);
          const bDate = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt || 0);
          
          // Debug logging for date comparison
          console.log(`Comparing dates: ${aDate.toISOString()} vs ${bDate.toISOString()}`);
          
          // Sort in ascending order (earliest first)
          return aDate.getTime() - bDate.getTime();
        });
        
        const original = sortedRecords[0];
        const duplicates = sortedRecords.slice(1);
        
        console.log(`Original record (earliest): ${original.createdAt ? original.createdAt.toISOString() : 'No date'}`);
        console.log(`Duplicate records:`, duplicates.map(d => d.createdAt ? d.createdAt.toISOString() : 'No date'));
        
        duplicateGroups.push({
          type: 'Duplicate ID',
          field: 'id',
          value: id,
          count: group.length,
          original: original,
          duplicates: duplicates,
          records: group
        });
      }
    });
    
    setDuplicates(duplicateGroups);
    
    // Debug: Log all duplicate groups to verify sorting
    console.log('=== DUPLICATE DETECTION DEBUG ===');
    duplicateGroups.forEach((group, idx) => {
      console.log(`Group ${idx + 1} - ID: ${group.value}`);
      console.log('Records sorted by createdAt (earliest first):');
      group.records.forEach((record, recordIdx) => {
        const dateStr = record.createdAt ? record.createdAt.toISOString() : 'No date';
        const isOriginal = record === group.original;
        console.log(`  ${recordIdx + 1}. ${dateStr} ${isOriginal ? '← ORIGINAL' : '← DUPLICATE'}`);
      });
      console.log('---');
    });
    console.log('=== END DEBUG ===');
  };

  // Generate CSV content from data
  const generateCSV = (data) => {
    if (data.length === 0) return '';
    
    const headers = allFields;
    const csvRows = [headers.join(',')];
    
    data.forEach(record => {
      const row = headers.map(header => {
        const value = record[header];
        if (value === null || value === undefined) return '';
        if (value instanceof Date) return value.toISOString();
        if (typeof value === 'object') return JSON.stringify(value);
        return `"${value.toString().replace(/"/g, '""')}"`;
      });
      csvRows.push(row.join(','));
    });
    
    return csvRows.join('\n');
  };
  
  // Download CSV file
  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Clear contamination status for all events
  const clearContaminationStatus = async () => {
    if (!window.confirm('Are you sure you want to clear the contamination status for all events? This will reset all "not_contaminated" flags.')) {
      return;
    }
    
    try {
      setIsDeleting(true);
      setDeleteProgress({ current: 0, total: data.length, message: 'Clearing contamination status...' });
      
      const collectionName = getDatabaseCollection();
      const updatePromises = data.map(async (record, index) => {
        if (record.not_contaminated) {
          await updateDoc(doc(database, collectionName, record.firestoreId), {
            not_contaminated: false,
            updatedAt: new Date()
          });
          
          setDeleteProgress(prev => ({
            ...prev,
            current: index + 1,
            message: `Clearing contamination status for event ${index + 1}...`
          }));
        }
      });
      
      await Promise.all(updatePromises);
      
      // Update local state
      setData(prevData => prevData.map(item => ({ ...item, not_contaminated: false })));
      
      // Update duplicates state
      setDuplicates(prevDuplicates => prevDuplicates.map(group => ({
        ...group,
        records: group.records.map(item => ({ ...item, not_contaminated: false })),
        original: { ...group.original, not_contaminated: false },
        duplicates: group.duplicates.map(item => ({ ...item, not_contaminated: false }))
      })));
      
      setDeleteProgress({ current: 0, total: 0, message: '' });
      alert('Successfully cleared contamination status for all events');
      
    } catch (error) {
      console.error('Error clearing contamination status:', error);
      alert(`Error clearing contamination status: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // Delete ALL duplicate groups at once with MEGA BATCH processing
  const deleteAllDuplicates = async () => {
    const totalDuplicates = duplicates.reduce((sum, group) => sum + group.duplicates.length, 0);
    
    if (!window.confirm(`Are you sure you want to delete ALL ${totalDuplicates} duplicate records across ${duplicates.length} duplicate groups? This will keep only the original records.`)) {
      return;
    }
    
    try {
      setIsDeleting(true);
      setDeleteProgress({ 
        current: 0, 
        total: totalDuplicates, 
        message: `Preparing MEGA batch delete of ${totalDuplicates} duplicates...` 
      });
      
      const collectionName = getDatabaseCollection();
      
      // MEGA BATCH DELETE: Use multiple batches for very large deletions
      const batchSize = 500; // Firestore batch limit is 500 operations
      let processedCount = 0;
      const updateDeleteProgress = (currentCount) => {
        setDeleteProgress(prev => ({
          ...prev,
          current: currentCount,
          message: `Deleted ${currentCount} of ${totalDuplicates} duplicates...`
        }));
      };
      
      for (const duplicateGroup of duplicates) {
        if (stopRequestedRef.current) break;
        
        // Split large groups into multiple batches if needed
        const duplicatesToDelete = duplicateGroup.duplicates;
        
        for (let i = 0; i < duplicatesToDelete.length; i += batchSize) {
          const batch = writeBatch(database);
          const currentBatch = duplicatesToDelete.slice(i, i + batchSize);
          
          currentBatch.forEach(record => {
            const docRef = doc(database, collectionName, record.firestoreId);
            batch.delete(docRef);
          });
          
          // Execute this batch
          await batch.commit();
          processedCount += currentBatch.length;
          
          updateDeleteProgress(processedCount);
          
          console.log(`Batch completed: ${processedCount}/${totalDuplicates} duplicates deleted`);
        }
      }
      
      console.log(`MEGA batch delete completed! Deleted ${processedCount} duplicates`);
      
      // MARK ALL REMAINING ORIGINALS AS CLEAN
      setDeleteProgress(prev => ({
        ...prev,
        message: `Marking remaining original events as clean...`
      }));
      
      console.log(`Marking ${duplicates.length} remaining original events as not_contaminated (clean)`);
      
      // Create a batch to mark all originals as clean
      const markCleanBatch = writeBatch(database);
      duplicates.forEach(duplicateGroup => {
        const originalDocRef = doc(database, collectionName, duplicateGroup.original.firestoreId);
        markCleanBatch.update(originalDocRef, {
          not_contaminated: true,
          updatedAt: new Date()
        });
      });
      
      // Execute the batch to mark all originals as clean
      await markCleanBatch.commit();
      
      console.log(`Marked all ${duplicates.length} remaining original events as clean`);
      
      // Refresh data to show updated state
      await fetchData();
      
      setDeleteProgress({ current: 0, total: 0, message: '' });
      alert(`Successfully deleted ALL ${processedCount} duplicate records and marked all remaining originals as clean!`);
      
    } catch (error) {
      console.error('Error deleting all duplicates:', error);
      alert(`Error deleting all duplicates: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // Delete duplicate records (keep the original) with FAST BATCH processing
  const deleteDuplicates = async (duplicateGroup) => {
    if (!window.confirm(`Are you sure you want to delete ${duplicateGroup.duplicates.length} duplicate records for ID "${duplicateGroup.value}"? This will keep only the original record.`)) {
      return;
    }
    
    try {
      setIsDeleting(true);
      setDeleteProgress({ 
        current: 0, 
        total: duplicateGroup.duplicates.length, 
        message: `Preparing batch delete for ID: ${duplicateGroup.value}` 
      });
      
      const collectionName = getDatabaseCollection();
      
      // FAST BATCH DELETE: Use Firestore batch operations for maximum speed
      const batch = writeBatch(database);
      
      // Add all delete operations to the batch
      duplicateGroup.duplicates.forEach((record, index) => {
        const docRef = doc(database, collectionName, record.firestoreId);
        batch.delete(docRef);
        
        // Update progress as we build the batch
        setDeleteProgress(prev => ({
          ...prev,
          current: index + 1,
          message: `Preparing batch delete: ${index + 1} of ${duplicateGroup.duplicates.length}`
        }));
      });
      
      setDeleteProgress(prev => ({
        ...prev,
        message: `Executing batch delete of ${duplicateGroup.duplicates.length} duplicates...`
      }));
      
      console.log(`Executing batch delete of ${duplicateGroup.duplicates.length} duplicates for ID: ${duplicateGroup.value}`);
      
      // Execute the batch - ALL deletions happen in one atomic operation
      await batch.commit();
      
      console.log(`Batch delete completed successfully for ID: ${duplicateGroup.value}`);
      
      // MARK THE ORIGINAL AS CLEAN: Update the remaining original event to not_contaminated: true
      const originalDocRef = doc(database, collectionName, duplicateGroup.original.firestoreId);
      await updateDoc(originalDocRef, {
        not_contaminated: true,
        updatedAt: new Date()
      });
      
      console.log(`Marked original event ${duplicateGroup.original.firestoreId} as not_contaminated (clean)`);
      
      // Remove from local state
      setData(prevData => prevData.filter(record => 
        !duplicateGroup.duplicates.some(dup => dup.firestoreId === record.firestoreId)
      ));
      
      // Remove from duplicates list
      setDuplicates(prevDuplicates => prevDuplicates.filter(group => group.value !== duplicateGroup.value));
      
      setDeleteProgress({ current: 0, total: 0, message: '' });
      alert(`Successfully deleted ${duplicateGroup.duplicates.length} duplicate records for ID "${duplicateGroup.value}" and marked the original as clean!`);
      
    } catch (error) {
      console.error('Error deleting duplicates:', error);
      alert(`Error deleting duplicates: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = data;
    
    // Apply duplicate filter
    if (showOnlyDuplicates) {
      const duplicateIds = new Set();
      duplicates.forEach(group => {
        group.records.forEach(record => {
          duplicateIds.add(record.firestoreId);
        });
      });
      filtered = filtered.filter(record => duplicateIds.has(record.firestoreId));
    }
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(record => {
        if (filterField === 'all') {
          return Object.values(record).some(value => 
            value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
          );
        } else if (filterField === 'title') {
          return record.title && record.title.toLowerCase().includes(searchTerm.toLowerCase());
        } else if (filterField === 'description') {
          return record.description && record.description.toLowerCase().includes(searchTerm.toLowerCase());
        } else if (filterField === 'id') {
          return record.id && record.id.toLowerCase().includes(searchTerm.toLowerCase());
        }
        return false;
      });
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle null/undefined values
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';
      
      // Handle dates
      if (aVal instanceof Date && bVal instanceof Date) {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // Handle strings and numbers
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      return 0;
    });
    
    return filtered;
  }, [data, searchTerm, filterField, sortField, sortDirection, showOnlyDuplicates, duplicates]);

  // Pagination
  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredAndSortedData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredAndSortedData, page, rowsPerPage]);

  // Toggle row expansion
  const toggleRowExpansion = (firestoreId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(firestoreId)) {
      newExpanded.delete(firestoreId);
    } else {
      newExpanded.add(firestoreId);
    }
    setExpandedRows(newExpanded);
  };

  // Format field value for display
  const formatFieldValue = (value, fieldName) => {
    if (value === null || value === undefined) {
      return <em style={{ color: '#999' }}>null</em>;
    }
    
    if (value instanceof Date) {
      return value.toLocaleString();
    }
    
    if (typeof value === 'boolean') {
      return value ? '✓' : '✗';
    }
    
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    
    if (typeof value === 'object') {
      return <pre style={{ fontSize: '12px', margin: 0 }}>{JSON.stringify(value, null, 2)}</pre>;
    }
    
    return value.toString();
  };

  // Get all unique field names from the data
  const allFields = useMemo(() => {
    const fields = new Set();
    data.forEach(record => {
      Object.keys(record).forEach(key => fields.add(key));
    });
    return Array.from(fields).sort();
  }, [data]);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [fetchData, user]);

  if (!user) {
    return (
      <Alert severity="warning">
        Please log in to access the database analysis.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px" gap={2}>
        <CircularProgress />
        {fetchProgress.total > 0 && (
          <Box textAlign="center">
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {fetchProgress.message}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Progress: {fetchProgress.current} of {fetchProgress.total}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={(fetchProgress.current / fetchProgress.total) * 100} 
              sx={{ mt: 1, width: 300 }}
            />
            {isFetching && (
              <Button
                variant="contained"
                color="error"
                onClick={stopFetch}
                sx={{ mt: 2 }}
                startIcon={<Error />}
              >
                Stop Fetching
              </Button>
            )}
          </Box>
        )}
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" onClose={() => setError(null)}>
        Error loading data: {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Delete Progress Bar */}
      {isDeleting && deleteProgress.total > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box sx={{ width: '100%' }}>
            <Typography variant="body2" gutterBottom>
              {deleteProgress.message}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={(deleteProgress.current / deleteProgress.total) * 100} 
              sx={{ mt: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              Progress: {deleteProgress.current} of {deleteProgress.total}
            </Typography>
          </Box>
        </Alert>
      )}

      {/* Duplicates Summary */}
      {duplicates.length > 0 && (
        <Alert severity={stopRequested ? "info" : "warning"} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {stopRequested ? `Analysis stopped by user - ${duplicates.length} duplicate ID groups completed` : `Found ${duplicates.length} duplicate ID groups`}
            </Typography>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={clearContaminationStatus}
              disabled={isDeleting}
              sx={{ mr: 1 }}
            >
              Clear All Contamination Status
            </Button>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={() => deleteAllDuplicates()}
              disabled={isDeleting}
              startIcon={<Delete />}
            >
              Delete ALL Duplicates
            </Button>
          </Box>
          
          {stopRequested && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              The fetch process was stopped manually. You can still analyze and delete duplicates for the completed groups.
            </Typography>
          )}
          
          {/* Sort duplicates: clean events first, then contaminated */}
          {duplicates
            .sort((a, b) => {
              // Sort by contamination status first (clean events on top)
              const aHasClean = a.records.some(r => r.not_contaminated);
              const bHasClean = b.records.some(r => r.not_contaminated);
              if (aHasClean && !bHasClean) return -1;
              if (!aHasClean && bHasClean) return 1;
              return 0;
            })
            .map((group, index) => (
            <Box key={index} sx={{ 
              mt: 2, 
              p: 2, 
              border: group.records.some(r => r.not_contaminated) ? '1px solid #4caf50' : '1px solid #ff9800', 
              borderRadius: 1, 
              backgroundColor: group.records.some(r => r.not_contaminated) ? '#e8f5e8' : '#fff3e0' 
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  ID: {group.value} ({group.count} records)
                  {group.records.some(r => r.not_contaminated) && (
                    <Chip 
                      label="Has Clean Events" 
                      color="success" 
                      size="small" 
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={() => deleteDuplicates(group)}
                  startIcon={<Delete />}
                  disabled={isDeleting}
                >
                  Delete {group.duplicates.length} Duplicates
                </Button>
              </Box>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Original (earliest createdAt): {group.original.createdAt ? group.original.createdAt.toLocaleString() : 'Unknown'}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Duplicates to remove: {group.duplicates.length} records
              </Typography>
              
              {/* Debug information - show all dates in the group */}
              <Box sx={{ mt: 1, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  All dates in this group (sorted by earliest first):
                </Typography>
                {group.records.map((record, idx) => (
                  <Typography key={idx} variant="caption" display="block" sx={{ 
                    color: record === group.original ? '#2e7d32' : '#d32f2f',
                    fontWeight: record === group.original ? 'bold' : 'normal'
                  }}>
                    {idx + 1}. {record.createdAt ? record.createdAt.toLocaleString() : 'No date'} 
                    {record === group.original ? ' ← ORIGINAL (earliest)' : ' ← DUPLICATE'}
                    {record.not_contaminated && ' ✓ CLEAN'}
                  </Typography>
                ))}
              </Box>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                <Chip 
                  label="Original (Keep)"
                  color="success"
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setExpandedRows(new Set([group.original.firestoreId]));
                    setTimeout(() => {
                      const element = document.getElementById(`record-${group.original.firestoreId}`);
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    }, 100);
                  }}
                  sx={{ cursor: 'pointer' }}
                />
                {group.duplicates.map((dup, dupIndex) => (
                  <Chip 
                    key={dupIndex}
                    label={`Duplicate ${dupIndex + 1} (Delete)`}
                    color="error"
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setExpandedRows(new Set([dup.firestoreId]));
                      setTimeout(() => {
                        const element = document.getElementById(`record-${dup.firestoreId}`);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }, 100);
                    }}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
          ))}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Click on chips to highlight and expand records. Use delete buttons to remove duplicate records (keeping only the original).
            Green groups have events marked as "not contaminated" (clean).
          </Typography>
        </Alert>
      )}

      {/* Filters and Search */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          label="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
          }}
        />
        
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Filter Field</InputLabel>
          <Select
            value={filterField}
            onChange={(e) => setFilterField(e.target.value)}
            label="Filter Field"
          >
            <MenuItem value="all">All Fields</MenuItem>
            <MenuItem value="title">Title</MenuItem>
            <MenuItem value="description">Description</MenuItem>
            <MenuItem value="id">ID</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Sort Field</InputLabel>
          <Select
            value={sortField}
            onChange={(e) => setSortField(e.target.value)}
            label="Sort Field"
          >
            {allFields.map(field => (
              <MenuItem key={field} value={field}>{field}</MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <IconButton
          onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
          size="small"
        >
          <Sort sx={{ transform: sortDirection === 'desc' ? 'rotate(180deg)' : 'none' }} />
        </IconButton>
        
        <Button
          variant="outlined"
          onClick={fetchData}
          size="small"
        >
          Refresh Data
        </Button>
        
        <Button
          variant={showOnlyDuplicates ? "contained" : "outlined"}
          onClick={() => setShowOnlyDuplicates(!showOnlyDuplicates)}
          size="small"
          color="warning"
          startIcon={<Warning />}
        >
          {showOnlyDuplicates ? 'Show All' : 'Show Only Duplicates'}
        </Button>
        
        <Button
          variant="outlined"
          onClick={() => {
            const csvContent = generateCSV(filteredAndSortedData);
            downloadCSV(csvContent, `database-analysis-${new Date().toISOString().split('T')[0]}.csv`);
          }}
          size="small"
          startIcon={<Download />}
        >
          Export CSV
        </Button>
      </Box>

      {/* Data Summary */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {paginatedData.length} of {filteredAndSortedData.length} records
          {searchTerm && ` (filtered by "${searchTerm}")`}
        </Typography>
      </Box>

      {/* Data Table */}
      <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox"></TableCell>
              {allFields.map(field => (
                <TableCell key={field}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {field}
                    {field === sortField && (
                      <Sort 
                        sx={{ 
                          fontSize: '16px',
                          transform: sortDirection === 'desc' ? 'rotate(180deg)' : 'none'
                        }} 
                      />
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((record, index) => (
              <React.Fragment key={record.firestoreId}>
                <TableRow 
                  id={`record-${record.firestoreId}`}
                  hover
                  sx={{ 
                    backgroundColor: duplicates.some(d => 
                      d.records.some(r => r.firestoreId === record.firestoreId)
                    ) ? '#fff3cd' : 'inherit'
                  }}
                >
                  <TableCell padding="checkbox">
                    <IconButton
                      size="small"
                      onClick={() => toggleRowExpansion(record.firestoreId)}
                    >
                      {expandedRows.has(record.firestoreId) ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </TableCell>
                  {allFields.map(field => (
                    <TableCell key={field} sx={{ maxWidth: 200, wordBreak: 'break-word' }}>
                      {formatFieldValue(record[field], field)}
                    </TableCell>
                  ))}
                </TableRow>
                
                {/* Expanded row with full details */}
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={allFields.length + 1}>
                    <Collapse in={expandedRows.has(record.firestoreId)} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 1, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          Full Record Details
                        </Typography>
                        <pre style={{ fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(record, null, 2)}
                        </pre>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={filteredAndSortedData.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[50, 100, 200, 500]}
      />
    </Box>
  );
};

export default DataAnalysisTable;
